// ---------------------------------------------------------------------------
// Story mode narrative events and progression
// ---------------------------------------------------------------------------

export interface StoryEvent {
  id: string;
  trigger: StoryTrigger;
  title: string;
  text: string[];          // dialogue lines shown sequentially
  speaker?: string;        // who is speaking (narrator, rival, etc.)
  reward?: StoryReward;
  nextEvent?: string;      // chains to another event
}

export interface StoryTrigger {
  type: 'day' | 'harvest' | 'battle_win' | 'farm_expand' | 'night_survive' | 'plant_unlock' | 'immediate';
  value?: number;          // e.g., day number, harvest count
  plantId?: string;        // for plant_unlock triggers
}

export interface StoryReward {
  seeds?: { plantId: string; quantity: number }[];
  coins?: number;
  unlockPlant?: string;
  expandFarm?: boolean;
}

export const STORY_EVENTS: StoryEvent[] = [
  // === PROLOGUE ===
  {
    id: 'intro',
    trigger: { type: 'immediate' },
    title: 'A New Beginning',
    speaker: 'Narrator',
    text: [
      'You arrive at an abandoned farm on the edge of the Wildlands.',
      'Strange plants grow here — plants with power.',
      'They say a rival farmer named Thornwick has been hoarding the rarest species...',
      'And at night, savage plant monsters crawl from the dark forest.',
      'Grow strong. Defend your farm. Challenge Thornwick. Become the PlantWars champion.',
    ],
    reward: {
      seeds: [{ plantId: 'sunflower', quantity: 5 }, { plantId: 'fire_pepper', quantity: 3 }],
    },
    nextEvent: 'first_plant',
  },

  // === CHAPTER 1: LEARNING ===
  {
    id: 'first_plant',
    trigger: { type: 'day', value: 1 },
    title: 'Getting Started',
    speaker: 'Narrator',
    text: [
      'Plant your seeds in the farm plots and water them.',
      'Different plants grow at different speeds — some prefer day, others night.',
      'Use E or tap the Use button to interact with farm plots.',
      'Select seeds from your hotbar (keys 1-8) before planting.',
    ],
  },
  {
    id: 'first_harvest',
    trigger: { type: 'harvest', value: 1 },
    title: 'First Harvest!',
    speaker: 'Narrator',
    text: [
      'Excellent! You harvested your first plant!',
      'Harvested plants become seeds you can replant, and warriors for battle.',
      'Each plant species has unique attack and defense stats.',
      'Keep growing — you\'ll need a strong army for what comes at night...',
    ],
  },

  // === CHAPTER 2: FIRST NIGHT ===
  {
    id: 'first_night',
    trigger: { type: 'day', value: 2 },
    title: 'Nightfall',
    speaker: 'Narrator',
    text: [
      'The sun is setting. Darkness approaches...',
      'At night, plant monsters emerge from the Wildlands to attack your farm!',
      'Use your sword (SPACE or tap Attack) to fight them off.',
      'If monsters reach your crops, they\'ll destroy them!',
      'Survive until dawn to earn rewards.',
    ],
  },
  {
    id: 'first_night_survived',
    trigger: { type: 'night_survive', value: 1 },
    title: 'Dawn Breaks',
    speaker: 'Narrator',
    text: [
      'You survived your first night! The monsters retreat at dawn.',
      'Each night gets harder. Grow stronger plants and upgrade your sword.',
      'You\'ve earned some coins from defeated monsters — use them to expand your farm.',
    ],
    reward: { coins: 20 },
  },

  // === CHAPTER 3: RIVAL APPEARS ===
  {
    id: 'rival_intro',
    trigger: { type: 'day', value: 4 },
    title: 'Thornwick',
    speaker: 'Thornwick',
    text: [
      'Well, well... a new farmer in MY territory.',
      'I\'m Thornwick, and these lands belong to me.',
      'I see you\'ve been growing some pathetic little plants...',
      'My Shadow Vines and Moonblooms would crush them in battle.',
      'Prove you\'re worthy — or leave before the real dangers come.',
    ],
  },
  {
    id: 'rival_challenge_1',
    trigger: { type: 'day', value: 7 },
    title: 'First Challenge',
    speaker: 'Thornwick',
    text: [
      'You\'re still here? Impressive... or foolish.',
      'Let\'s see what your plants can do. I challenge you to a Plant Battle!',
      'Meet me at the battle arena — send your best plant against mine.',
      'If you win, I\'ll share one of my rare Moonbloom seeds.',
    ],
    reward: {
      seeds: [{ plantId: 'moonbloom', quantity: 2 }],
      unlockPlant: 'moonbloom',
    },
  },

  // === CHAPTER 4: GROWTH ===
  {
    id: 'farm_expand_hint',
    trigger: { type: 'day', value: 5 },
    title: 'Room to Grow',
    speaker: 'Narrator',
    text: [
      'Your farm is getting crowded. You can spend coins to expand your growing area.',
      'Look for the expansion markers at the edges of your farm.',
      'A bigger farm means more plants, more harvests, and a stronger army.',
    ],
  },
  {
    id: 'first_expansion',
    trigger: { type: 'farm_expand' },
    title: 'Expanded!',
    speaker: 'Narrator',
    text: [
      'Your farm grows larger! More plots means more possibilities.',
      'Try growing night-type plants like Moonbloom and Shadow Vine.',
      'Combining different plant types can create powerful hybrids...',
    ],
  },
  {
    id: 'unlock_shadow_vine',
    trigger: { type: 'plant_unlock', plantId: 'shadow_vine' },
    title: 'Shadow Vine Discovered',
    speaker: 'Narrator',
    text: [
      'You\'ve discovered the Shadow Vine! This sinister plant thrives in darkness.',
      'Shadow Vines grow fastest at night and have the Shadow Snare ability.',
      'Try combining it with a Fire Pepper for something... explosive.',
    ],
  },

  // === CHAPTER 5: FINAL BATTLE ===
  {
    id: 'rival_challenge_final',
    trigger: { type: 'day', value: 14 },
    title: 'The Final Challenge',
    speaker: 'Thornwick',
    text: [
      'I\'ll admit it — you\'ve become a worthy rival.',
      'But I\'ve been saving my ultimate plant for this moment.',
      'One final battle. Winner takes all.',
      'My Infernal Creeper against your best. No holding back.',
      'If you win, the Wildlands are yours. If you lose... well, let\'s not think about that.',
    ],
  },
  {
    id: 'victory',
    trigger: { type: 'battle_win', value: 5 },
    title: 'Champion of the Wildlands!',
    speaker: 'Narrator',
    text: [
      'You\'ve defeated Thornwick and proven yourself the true PlantWars champion!',
      'The Wildlands are yours to tend and protect.',
      'But the night monsters grow ever stronger...',
      'The battle for the garden never truly ends.',
      'Continue playing to discover all plant combinations and face greater challenges!',
    ],
    reward: {
      coins: 100,
      seeds: [{ plantId: 'shadow_vine', quantity: 5 }],
    },
  },
];
